// Copy for the Learn more modal on the cloud storage cards. Content only — it mirrors the Google
// Drive and SharePoint help centre article, so keep the two in step when either changes.

import { ProviderType } from "./types";

// A block with no `subtitle` continues the one above it, so a section can pair a list of steps with
// a closing paragraph without that paragraph becoming its own heading.
type CmsContentBlock = {
  subtitle?: string;
  text?: string;
  steps?: string[];
};

export type CmsLearnMoreContent = {
  modalTitle: string;
  intro: string[];
  contentBlocks: CmsContentBlock[];
};

export const TRUST_CENTRE_URL = "https://trust.stotles.com/";

// Only the cloud storage providers have this copy — the CRM providers share the `ProviderType`
// union but connect through their own cards.
export const CMS_LEARN_MORE_CONTENT: Partial<Record<ProviderType, CmsLearnMoreContent>> = {
  googledrive: {
    modalTitle: "Sync your content from Google Drive",
    intro: [
      "Cloud storage integrations are available to Bid Studio customers.",
      "Connecting Google Drive syncs your content library files to Stotles, so you don't have to upload them again every time your content changes. Stotles AI in Bid Studio always works from your latest content.",
    ],
    contentBlocks: [
      {
        subtitle: "How to set it up",
        steps: [
          "Click Connect on this card, then Connect again.",
          "Sign in to your Google Drive account.",
          "Choose the drive your content is stored in.",
          "Select the files and folders you want to sync to Stotles.",
        ],
      },
      {
        text: "Syncing can take a while, and you can leave these pages while it runs. You'll see how many files have been processed and any errors along the way, and we'll email you once your content is available on Stotles.",
      },
      {
        subtitle: "Keeping content up to date",
        text: "Every file and folder you sync is monitored for changes. Edits to a file, files added to a synced folder, and files removed from one are all picked up, usually within a few minutes.",
      },
      {
        subtitle: "Security and access",
        text: "You need access to Bid Studio to set up a cloud storage integration, and anyone with access to the Google Drive account can connect it to Stotles. Files synced from Google Drive can be read by anyone at your company on Stotles. Disconnecting the integration deletes every synced file from your Stotles account.",
      },
    ],
  },
  sharepoint: {
    modalTitle: "Sync your content from SharePoint",
    intro: [
      "Cloud storage integrations are available to Bid Studio customers.",
      "Connecting SharePoint syncs your content library files to Stotles, so you don't have to upload them again every time your content changes. Stotles AI in Bid Studio always works from your latest content.",
    ],
    contentBlocks: [
      {
        subtitle: "How to set it up",
        steps: [
          "Click Connect on this card, then Connect again.",
          "Sign in to your Microsoft SharePoint account.",
          "Choose the SharePoint site your content is stored in.",
          "Select the files and folders you want to sync to Stotles.",
        ],
      },
      {
        text: "Syncing can take a while, and you can leave these pages while it runs. You'll see how many files have been processed and any errors along the way, and we'll email you once your content is available on Stotles.",
      },
      {
        subtitle: "Keeping content up to date",
        text: "Every file and folder you sync is monitored for changes. Edits to a file, files added to a synced folder, and files removed from one are all picked up, usually within a few minutes.",
      },
      {
        subtitle: "Security and access",
        text: "You need access to Bid Studio to set up a cloud storage integration, and anyone with access to the SharePoint site can connect it to Stotles. Files synced from SharePoint can be read by anyone at your company on Stotles. Disconnecting the integration deletes every synced file from your Stotles account.",
      },
    ],
  },
};
